import {updateTrackingConfiguration} from '@js/actions/trackingActions';

import PWAManager from '@js/modules/pwaManager';


class TrackingManager {
    _initialTrackingDelay = 750; // in ms, settle delay before starting the dwell timer
    _dwellDelay = 2000;          // in ms, foreground time before a no-interaction visit is confirmed

    // Genuine human interactions that confirm a visit.
    _engagementEvents = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'wheel', 'touchstart'];
    _confirmed = false;

    /* Public methods
    ******************** */
    // A visit is confirmed as human only on a genuine signal — a trusted user
    // interaction, or enough foreground dwell time — never merely because the
    // page loaded. Headless bots that just load and leave stay unconfirmed
    // (validated:false server-side). Cookie refusal / adblock are NOT treated as
    // bot signals: a real user who rejects cookies still counts.
    initialize = (forceRequest) => {
        // A consent accept/deny is itself a genuine human interaction.
        if (forceRequest) {
            this._confirmVisit();
            return;
        }

        if (this._alreadySent()) return;

        // Confirm on the first genuine (trusted) interaction...
        this._bindEngagement();

        // ...or after enough foreground dwell time, unless the client shows
        // strong bot characteristics — then only a trusted interaction confirms.
        setTimeout(() => {
            if (this._confirmed || this._hasBotCharacteristics()) return;

            const focused = typeof document.hasFocus !== 'function' || document.hasFocus();

            if (document.visibilityState === 'visible' && focused) {
                this._confirmVisit();
            }
        }, this._initialTrackingDelay + this._dwellDelay);
    };

    /* Private methods
    ******************** */
    _onEngagement = (event) => {
        // Ignore synthetic events dispatched by bots.
        if (event && event.isTrusted === false) return;

        this._confirmVisit();
    };

    _bindEngagement = () => {
        // No { once: true }: an untrusted (bot) event would otherwise consume the
        // listener before a later trusted one. Removed on confirmation instead.
        this._engagementEvents.forEach((name) => window.addEventListener(name, this._onEngagement, {capture: true, passive: true}));
    };

    _unbindEngagement = () => {
        this._engagementEvents.forEach((name) => window.removeEventListener(name, this._onEngagement, true));
    };

    // Send the visit confirmation to the server (validated = true).
    _confirmVisit = () => {
        if (this._confirmed) return;

        this._confirmed = true;
        this._unbindEngagement();

        if (this._alreadySent()) return;

        const properties = {
            valid: true
        };

        if (PWAManager.getPWADisplayMode() === PWAManager.MODE.PWA) {
            properties.pwa = true;
        }

        updateTrackingConfiguration(properties)
            .then(() => {
                if (!this._rejectCookies()) {
                    document.cookie = 'configurationSet=true;path=/';
                }
            })
            .catch(() => {
                if (!this._rejectCookies()) {
                    document.cookie = '';
                }
            });

        if (!this._rejectCookies()) {
            document.cookie = 'configurationSet=true;path=/';
        }
    };

    // Strong, low-false-positive signals that the client is an automated agent
    // (headless browser / automation framework) rather than a human.
    _hasBotCharacteristics = () => {
        try {
            const nav = window.navigator || {};

            // Explicit automation flag set by Selenium / Puppeteer / Playwright.
            if (nav.webdriver === true) return true;

            // Globals injected by common automation / headless frameworks.
            const automationGlobals = [
                '_phantom', '__nightmare', 'callPhantom', '__phantomas',
                'domAutomation', 'domAutomationController',
                '__webdriver_evaluate', '__driver_evaluate',
                '__selenium_evaluate', '__selenium_unwrapped', '__fxdriver_evaluate'
            ];
            if (automationGlobals.some((key) => key in window)) return true;
            if (document.documentElement.getAttribute('webdriver') !== null) return true;

            // A real browser always exposes at least one preferred language and
            // a non-zero window / screen size.
            if (nav.languages && nav.languages.length === 0) return true;
            if (window.outerWidth === 0 && window.outerHeight === 0) return true;
            if (window.screen && window.screen.width === 0 && window.screen.height === 0) return true;

            return false;
        } catch (e) {
            // Never penalise a visit if detection itself fails.
            return false;
        }
    };

    _rejectCookies = () => {
        if (navigator.cookieEnabled) return false;
        if (!document.cookie.indexOf) return false;

        // set and read cookie
        document.cookie = 'cookietest=1';
        const ret = document.cookie.indexOf('cookietest=') === -1;

        // delete cookie
        document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';

        return ret;
    };

    _alreadySent = () => {
        return this._rejectCookies() ? false : document.cookie.indexOf('configurationSet=') !== -1;
    };
}

export default new TrackingManager();
